import React, { useState, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import DashboardLayout from '../layout/DashboardLayout';
import Dashboard from '../pages/Dashboard';
import Singlepartypage from '../pages/singlepartypage';
import Addparty from '../pages/addparty';
import Addnewparty from '../pages/addnewparty';
import Stocks from '../pages/stocks';
import Addstock from '../pages/addstock';
import Partylist from '../pages/partylist';
import Settings from '../pages/Settings';
import History from '../pages/history';
import Login from '../pages/Login';
import Register from '../pages/Register';
import ManageUsers from '../pages/ManageUsers';
import Logout from '../pages/Logout';
import BalanceSheet from '../pages/BalanceSheet';
import UserHistory from '../pages/userhistory';
import { TbHistory } from "react-icons/tb";
import SellStocksPage from '../pages/sellstocks';
import WithdrawlPage from '../pages/withdrawl';
import AddAmountPage from '../pages/addAmount';
import UpdateStockValuePage from '../pages/updateStockValue';
import StockManagment from '../pages/StockManagment';
import Calculation from '../components/calculations';
import CustomBalance from '../pages/MutualBalance';
import EditStocks from '../pages/EditSell';
import EditSell from '../pages/EditSell';
import EdidStcks from '../pages/EditStock';
import EdiStocks from '../pages/EditStock';
import Invoice from '../pages/Invoice';
import InterestCalculator from '../pages/InterestFormulla';
import UnifiedBalanceSheet from '../pages/UnifiedBalanceSheet';

// Preloader component — same as before, no change
const Preloader = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#B9DEFF] via-[#F3FFF1] to-[#FFE8B2]">
    <div className="max-w-md w-full px-8 py-8 bg-white/95 rounded-3xl shadow-2xl flex flex-col items-center text-center border-t-4 border-b-4 border-[#25b474] animate-fade-in-alt relative">
      <div className="mb-7 mt-1">
        <div className="mx-auto mb-3 flex items-center justify-center">
          <div className="relative">
            <svg className="animate-spin-gear h-12 w-12 text-[#2296ec] drop-shadow-lg" fill="none" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="18" stroke="#16c784" strokeWidth="4" opacity="0.23"/>
              <circle cx="24" cy="24" r="11" stroke="#ff405c" strokeWidth="3" strokeDasharray="51,19" />
              <rect x="20" y="5" width="8" height="8" rx="2" fill="#2296ec" className="animate-bounce-alt" />
            </svg>
            <span className="absolute bottom-[-12px] right-[-10px] animate-pop">
              <span role="img" aria-label="rocket" className="text-lg">🚀</span>
            </span>
          </div>
        </div>
        <div className="font-extrabold text-2xl tracking-tight text-[#25b474]">
          Stock Management
        </div>
        <div className="font-bold text-[#2296ec] tracking-wider text-md mt-0.5">
          Loading your dashboard...
        </div>
      </div>
      <div className="mt-7 flex flex-col items-center gap-5 hidden">
        {/* Basic messages/graphs hidden */}
      </div>
      <div className="mt-6 flex items-center justify-center gap-3 text-lg" aria-hidden>
        <span title="Syncing"><span role="img" aria-label="sync">🔄</span></span>
        <span title="Analyzing"><span role="img" aria-label="chart">📊</span></span>
        <span title="Security"><span role="img" aria-label="safe">🔒</span></span>
        <span title="Energy"><span role="img" aria-label="bolt">⚡️</span></span>
      </div>
    </div>
    <style>{`
      @keyframes fade-in-alt { from { opacity: 0; transform: scale(.94); } to { opacity: 1; transform: scale(1); } }
      .animate-fade-in-alt { animation: fade-in-alt 0.5s cubic-bezier(.25,1,.5,1); }
      @keyframes spin-gear { 100% { transform: rotate(360deg);} }
      .animate-spin-gear { animation: spin-gear 1.25s linear infinite;}
      @keyframes loader-progress {
        0% { width: 0%; background-color: #25b474;}
        20% { width: 45%; background-color: #25b474;}
        50% { width: 68%; background-color: #2296ec;}
        80% { width: 92%; background-color: #ffbc70;}
        100% { width: 100%; background-color: #16c784;}
      }
      .animate-loader-progress { animation: loader-progress 1.6s cubic-bezier(.28,.84,.42,1) infinite;}
      @keyframes bounce-alt {
        0%,100% { transform: translateY(1px);}
        50% { transform: translateY(-6px);}
      }
      .animate-bounce-alt {
        animation: bounce-alt 1.4s linear infinite;
      }
      @keyframes pop { 0% { transform: scale(.6);} 60% {transform:scale(1.12);} 100%{transform:scale(1);} }
      .animate-pop { animation: pop 0.6s .3s cubic-bezier(.28,.75,.68,1.75) both; }
    `}</style>
  </div>
);

// Custom preloader hook for the /add-party route: fetches party data before proceeding.
function useAddPartyPreloader() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [partyData, setPartyData] = useState<null | object>(null);
  const [delayDone, setDelayDone] = useState(false);

  useEffect(() => {
    // Only trigger on the "/add-party" route
    if (location.pathname === "/add-party") {
      setLoading(true);
      setDelayDone(false);

      // Trigger a 450ms timer in parallel with fetch
      const delayTimer = setTimeout(() => setDelayDone(true), 450);

      // Fetch from the backend before showing add-party page
      fetch("https://stockaadvikbackend.aadviktech.com/auth/addparty", {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(res => res.json())
        .then(data => {
          setPartyData(data);
        })
        .catch(() => {
          setPartyData({});
        });

      return () => {
        clearTimeout(delayTimer);
      };
    } else {
      // If not on /add-party route, reset
      setLoading(false);
      setDelayDone(false);
      setPartyData(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/add-party" && partyData !== null && delayDone) {
      // Both API and delay finished
      setLoading(false);
    } else if (location.pathname === "/add-party") {
      setLoading(true);
    }
  }, [partyData, delayDone, location.pathname]);

  return loading;
}

// Default page change preloader for all other pages
function usePagePreloader(delay = 400) {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Don't trigger for /add-party (handled by useAddPartyPreloader)
    if (location.pathname === "/add-party") {
      setLoading(false);
      return;
    }

    setLoading(true);
    const t = setTimeout(() => setLoading(false), delay);
    return () => clearTimeout(t);
  }, [location, delay]);

  return loading;
}

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

interface Props { }

function AllRoutes(props: Props) {
  // If on "/add-party", use the custom data-fetching preloader, else use default
  const addPartyPreloading = useAddPartyPreloader();
  const pagePreloading = usePagePreloader(450);
  const showPreloader = addPartyPreloading || pagePreloading;

  return (
    <>
      {showPreloader && <Preloader />}
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Protected Routes */}
        <Route element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route path="/" element={<Dashboard />} />
          <Route path="party/:id" element={<Singlepartypage />} />
          <Route path="/add-party" element={<Addparty />} />
          <Route path="/addnewparty" element={<Addnewparty />} />
          <Route path="/Stock" element={<Stocks />} />
          <Route path="/add-stock" element={<Addstock />} />
          <Route path="/parties-list" element={<Partylist />} />
          <Route path='/BalanceSheet' element={<BalanceSheet />} />
          <Route path='/interest-calc' element={<InterestCalculator />} />
          <Route path='/unified-balance-sheet' element={<UnifiedBalanceSheet />} />
  
          <Route path='/mutual-balance-sheet' element={<CustomBalance />} />
          <Route path='/history' element={<History />} >
            <Route path="/history/stock" element={<h1>help</h1>} />
          </Route>
          <Route path="/users" element={<ManageUsers />} />
          <Route path="/usershistory" element={<UserHistory />} />
          <Route path="/invoice" element={<Invoice />} />
          <Route path="/setting" element={<Settings />} />
          <Route path="/sell-stock" element={<SellStocksPage />} />
          <Route path="/edit-sell" element={<EditSell />} />
          <Route path="/edit-stock" element={<EdiStocks />} />
          <Route path="/withdraw" element={<WithdrawlPage />} />
          <Route path="/add-amount" element={<AddAmountPage />} />
          <Route path="/update-stock-value" element={<UpdateStockValuePage />} />
          <Route path="/stocks-name" element={<StockManagment />} />
          <Route path="/calculation" element={<Calculation />} />
          <Route path="/help" element={<h1>help</h1>} />
        </Route>
        <Route path="/logout" element={<Logout />} />
      </Routes>
    </>
  )
}

export default AllRoutes
